import React, { useEffect, useMemo, useState } from 'react';
import { Col, Divider, Form, Flex, Switch, message, Row } from 'antd';
import {
  CustomButton,
  CustomInput,
  CustomShipmentText,
  CustomTitle,
} from '@/components';
import CustomSelect from '@/components/CustomSelect';
import { useToken } from '@/common/helper';
import {
  CreateFreightMarkupRate,
  DeleteFreightMarkupRate,
  FreightMarkupRateCarrierOption,
  FreightMarkupRateData,
  FreightMarkupRateTeamOption,
  GetFreightMarkupRateOptions,
  UpdateFreightMarkupRate,
} from '@/apis/freightMarkupRates';

export type MarkupTeamRow = {
  team_id: string;
  team_name: string;
  team_color?: string | null;
  rates: FreightMarkupRateData[];
};

/** Seed markup % for UI prefill and suggested calc until a rate is saved. */
export const SEED_MARKUP_PERCENT = 50;

/** Sentinel service_level: one markup for every level on that team × courier. */
export const ALL_SERVICE_LEVELS = '*';

interface FreightMarkupRateFormProps {
  /** Customer row being edited (identity + display). */
  teamRow: MarkupTeamRow;
  /** Company rates — source of truth for the open editor (refreshed after saves). */
  allRates: FreightMarkupRateData[];
  onClose: () => void;
  onSaved: () => void;
}

type LevelFieldKey = string; // `${courierId}::${originalServiceLevel}`

function fieldKey(courierId: string, serviceLevel: string): LevelFieldKey {
  return `${courierId}::${serviceLevel}`;
}

function allFieldKey(courierId: string): LevelFieldKey {
  return fieldKey(courierId, ALL_SERVICE_LEVELS);
}

function formatMarkupPercent(value: string | number): string {
  const num = Number(value);
  if (Number.isNaN(num)) return SEED_MARKUP_PERCENT.toFixed(2);
  return num.toFixed(2);
}

/**
 * Team-scoped markup editor: FedEx / UPS × account service levels.
 * Layout follows TeamForm drawer chrome (section titles + dividers).
 */
const FreightMarkupRateForm: React.FC<FreightMarkupRateFormProps> = ({
  teamRow,
  allRates,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const { token: rawToken } = useToken();
  const token = rawToken ?? '';
  const [saving, setSaving] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState(false);
  const [teams, setTeams] = useState<FreightMarkupRateTeamOption[]>([]);
  const [carriers, setCarriers] = useState<FreightMarkupRateCarrierOption[]>(
    []
  );
  /** Per courier: one markup applies to all service levels. */
  const [allLevelsByCourier, setAllLevelsByCourier] = useState<
    Record<string, boolean>
  >({});

  const activeRates = useMemo(
    () => allRates.filter((rate) => rate.team_id === teamRow.team_id),
    [allRates, teamRow.team_id]
  );

  useEffect(() => {
    let cancelled = false;
    setOptionsLoading(true);
    setOptionsError(false);
    GetFreightMarkupRateOptions(token)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data;
        if (data?.is_success) {
          setTeams(data.data?.teams || []);
          setCarriers(data.data?.carriers || []);
        } else {
          setOptionsError(true);
          setTeams([]);
          setCarriers([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching rate options:', error);
        if (!cancelled) {
          setOptionsError(true);
          setTeams([]);
          setCarriers([]);
        }
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const values: Record<string, string | undefined> = {
      team_id: teamRow.team_id,
    };
    const existingByKey = new Map<string, FreightMarkupRateData>();
    const couriersWithWildcard = new Set<string>();
    const couriersWithExact = new Set<string>();
    for (const rate of activeRates) {
      existingByKey.set(fieldKey(rate.courier_id, rate.service_level), rate);
      if (rate.service_level === ALL_SERVICE_LEVELS) {
        couriersWithWildcard.add(rate.courier_id);
      } else {
        couriersWithExact.add(rate.courier_id);
      }
    }
    // "Same for all" only when * exists and there are no exact overrides.
    // Mixed * + exact is valid on the backend; enabling the switch would
    // delete those overrides on save.
    const nextAll: Record<string, boolean> = {};
    for (const courierId of couriersWithWildcard) {
      nextAll[courierId] = !couriersWithExact.has(courierId);
    }
    for (const carrier of carriers) {
      const allKey = allFieldKey(carrier.id);
      const allExisting = existingByKey.get(allKey);
      values[allKey] = allExisting
        ? formatMarkupPercent(allExisting.markup_percent)
        : formatMarkupPercent(SEED_MARKUP_PERCENT);
      for (const level of carrier.service_levels || []) {
        const key = fieldKey(carrier.id, level.original_service_level);
        const existing = existingByKey.get(key);
        const fallback = allExisting
          ? formatMarkupPercent(allExisting.markup_percent)
          : formatMarkupPercent(SEED_MARKUP_PERCENT);
        values[key] = existing
          ? formatMarkupPercent(existing.markup_percent)
          : fallback;
      }
    }
    setAllLevelsByCourier(nextAll);
    form.setFieldsValue(values);
  }, [activeRates, carriers, form, teamRow.team_id]);

  const rateByKey = useMemo(() => {
    const map = new Map<string, FreightMarkupRateData>();
    for (const rate of activeRates) {
      map.set(fieldKey(rate.courier_id, rate.service_level), rate);
    }
    return map;
  }, [activeRates]);

  const parsePercent = (
    raw: unknown,
    label: string
  ): number | null | 'invalid' => {
    const trimmed = raw === undefined || raw === null ? '' : String(raw).trim();
    if (trimmed === '') return null;
    const markupPercent = Number(trimmed);
    if (Number.isNaN(markupPercent) || markupPercent < 0) {
      message.error(`Invalid markup for ${label}`);
      return 'invalid';
    }
    return markupPercent;
  };

  const onFinish = async (values: Record<string, unknown>): Promise<void> => {
    if (optionsLoading || optionsError) {
      return;
    }
    const teamId = teamRow.team_id;

    const upserts: Array<{
      courier_id: string;
      service_level: string;
      markup_percent: number;
      existing?: FreightMarkupRateData;
    }> = [];
    const deletes: FreightMarkupRateData[] = [];

    for (const carrier of carriers) {
      const useAll = !!allLevelsByCourier[carrier.id];
      const levelRates = (carrier.service_levels || [])
        .map((level) =>
          rateByKey.get(fieldKey(carrier.id, level.original_service_level))
        )
        .filter((rate): rate is FreightMarkupRateData => !!rate);
      const allExisting = rateByKey.get(allFieldKey(carrier.id));

      if (useAll) {
        const parsed = parsePercent(
          values[allFieldKey(carrier.id)],
          `${carrier.bucket || carrier.name} (all service levels)`
        );
        if (parsed === 'invalid') return;
        if (parsed !== null) {
          upserts.push({
            courier_id: carrier.id,
            service_level: ALL_SERVICE_LEVELS,
            markup_percent: parsed,
            existing: allExisting,
          });
        } else if (allExisting) {
          // Blank field → drop the carrier-wide rate (falls back to seed).
          deletes.push(allExisting);
        }
        // Carrier-wide mode: drop per-level rows so they cannot override *.
        deletes.push(...levelRates);
      } else {
        if (allExisting) {
          deletes.push(allExisting);
        }
        for (const level of carrier.service_levels || []) {
          const key = fieldKey(carrier.id, level.original_service_level);
          const existing = rateByKey.get(key);
          const parsed = parsePercent(
            values[key],
            `${carrier.name} / ${level.display_name}`
          );
          if (parsed === 'invalid') return;
          if (parsed === null) {
            // Blank field → archive the exact rate so lookup falls back to
            // carrier-wide * or the 50% seed.
            if (existing) deletes.push(existing);
            continue;
          }
          upserts.push({
            courier_id: carrier.id,
            service_level: level.original_service_level,
            markup_percent: parsed,
            existing,
          });
        }
      }
    }

    setSaving(true);
    try {
      for (const row of deletes) {
        const res = await DeleteFreightMarkupRate(token, row.id);
        if (!res?.data?.data?.is_success) {
          throw new Error(res?.data?.data?.message || 'Failed to remove rate');
        }
      }
      for (const row of upserts) {
        const payload = {
          team_id: teamId,
          courier_id: row.courier_id,
          service_level: row.service_level,
          markup_percent: row.markup_percent,
        };
        const res = row.existing
          ? await UpdateFreightMarkupRate(token, row.existing.id, payload)
          : await CreateFreightMarkupRate(token, payload);
        if (!res?.data?.data?.is_success) {
          throw new Error(res?.data?.data?.message || 'Failed to save rate');
        }
      }
      message.success('Markup rates saved');
      onSaved();
      onClose();
    } catch (error) {
      const msg =
        (error as { message?: string })?.message ||
        (error as { data?: { data?: { message?: string } } })?.data?.data
          ?.message ||
        'Failed to save markup rates';
      message.error(msg);
      // Partial multi-request saves can leave the card half-updated; reload
      // so the form matches the server before the user retries.
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={onFinish}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <div className='drawer-title-header'>
        <CustomTitle
          text='Edit markup'
          fontSize={20}
          fontWeight='700'
          lineHeight='32px'
          color='var(--text-primary)'
          marginBottom={0}
        />
      </div>

      <div className='freight-markup-form-body custom-scrollbar'>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Form.Item
              name='team_id'
              label='Customer'
              className='custom-input form-input-style'
              style={{ marginBottom: 0 }}
            >
              <CustomSelect
                bgColor='var(--light-gray)'
                textColor='var(--text-primary)'
                height='36px'
                borderRadius='8px'
                border='none'
                disabled
                options={[
                  { value: teamRow.team_id, label: teamRow.team_name },
                  ...teams
                    .filter((team) => team.id !== teamRow.team_id)
                    .map((team) => ({
                      value: team.id,
                      label: team.name,
                    })),
                ]}
              />
            </Form.Item>
          </Col>

          {optionsLoading ? (
            <Col span={24}>
              <Divider className='notification-divider' />
              <CustomShipmentText
                text='Loading carriers…'
                fontSize='14px'
                lineHeight='20px'
                color='var(--text-secondary)'
              />
            </Col>
          ) : optionsError ? (
            <Col span={24}>
              <Divider className='notification-divider' />
              <CustomShipmentText
                text='Could not load carriers. Close and try again.'
                fontSize='14px'
                lineHeight='20px'
                color='var(--text-secondary)'
              />
            </Col>
          ) : carriers.length === 0 ? (
            <Col span={24}>
              <Divider className='notification-divider' />
              <CustomShipmentText
                text='No FedEx or UPS carriers are configured for this account.'
                fontSize='14px'
                lineHeight='20px'
                color='var(--text-secondary)'
              />
            </Col>
          ) : (
            carriers.map((carrier) => {
              const useAll = !!allLevelsByCourier[carrier.id];
              return (
                <React.Fragment key={carrier.id}>
                  <Col span={24}>
                    <Divider className='notification-divider' />
                  </Col>
                  <Col span={24}>
                    <Flex
                      align='center'
                      justify='space-between'
                      gap={16}
                      wrap='wrap'
                    >
                      <div>
                        <CustomShipmentText
                          text={carrier.bucket || carrier.name}
                          fontSize='18px'
                          fontWeight={700}
                          lineHeight='24px'
                        />
                        <div className='freight-markup-form-subtitle'>
                          <CustomShipmentText
                            text={
                              useAll
                                ? 'One markup % for all service levels'
                                : 'Markup % of courier cost by service level'
                            }
                            fontSize='14px'
                            lineHeight='20px'
                            color='var(--text-secondary)'
                          />
                        </div>
                      </div>
                      <Flex align='center' gap={8}>
                        <CustomShipmentText
                          text='Same for all'
                          fontSize='13px'
                          lineHeight='20px'
                          color='var(--text-secondary)'
                        />
                        <Switch
                          checked={useAll}
                          onChange={(checked) =>
                            setAllLevelsByCourier((prev) => ({
                              ...prev,
                              [carrier.id]: checked,
                            }))
                          }
                        />
                      </Flex>
                    </Flex>
                  </Col>
                  {useAll ? (
                    <Col span={12}>
                      <Form.Item
                        name={allFieldKey(carrier.id)}
                        label='All service levels'
                        className='custom-input form-input-style'
                        style={{ marginBottom: 0 }}
                      >
                        <CustomInput
                          inputMode='decimal'
                          placeholder={SEED_MARKUP_PERCENT.toFixed(2)}
                          suffix='%'
                          bgColor='var(--light-gray)'
                          textColor='var(--text-primary)'
                          height='36px'
                          borderRadius='8px'
                          border='none'
                        />
                      </Form.Item>
                    </Col>
                  ) : (carrier.service_levels || []).length === 0 ? (
                    <Col span={24}>
                      <CustomShipmentText
                        text={`No service levels found on this account for ${
                          carrier.bucket || carrier.name
                        }.`}
                        fontSize='14px'
                        lineHeight='20px'
                        color='var(--text-secondary)'
                      />
                    </Col>
                  ) : (
                    (carrier.service_levels || []).map((level) => (
                      <Col
                        span={12}
                        key={fieldKey(carrier.id, level.original_service_level)}
                      >
                        <Form.Item
                          name={fieldKey(
                            carrier.id,
                            level.original_service_level
                          )}
                          label={
                            level.display_name || level.original_service_level
                          }
                          className='custom-input form-input-style'
                          style={{ marginBottom: 0 }}
                        >
                          <CustomInput
                            inputMode='decimal'
                            placeholder={SEED_MARKUP_PERCENT.toFixed(2)}
                            suffix='%'
                            bgColor='var(--light-gray)'
                            textColor='var(--text-primary)'
                            height='36px'
                            borderRadius='8px'
                            border='none'
                          />
                        </Form.Item>
                      </Col>
                    ))
                  )}
                </React.Fragment>
              );
            })
          )}
        </Row>
      </div>

      <div className='drawer-custom-footer'>
        <Row gutter={[0, 8]} style={{ marginTop: 0 }}>
          <CustomButton
            text='Save'
            bgColor='#000'
            htmlType='submit'
            color='#fff'
            fontSize='16px'
            fontWeight='600'
            boxShadow='none'
            height='48px'
            borderRadius='8px'
            width='100%'
            disabled={
              saving || optionsLoading || optionsError || carriers.length === 0
            }
          />
          <CustomButton
            text='Cancel'
            bgColor='transparent'
            color='var(--text-primary)'
            fontSize='16px'
            fontWeight='600'
            boxShadow='none'
            height='48px'
            width='100%'
            border='none'
            onClick={onClose}
          />
        </Row>
      </div>
    </Form>
  );
};

export default FreightMarkupRateForm;
